import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useAuth } from "../../context/AuthContext";
import { useAlert } from "../../context/AlertContext";
import { useLoading } from "../../context/LoadingContext";
import { getServiceRequest } from "../../commons/ServiceRequest";
import { autoDisplayValue, formatSplitKey } from "../../commons/FormatValue";
import { initializeSetOptionDisabled } from "../../utils/InitializeSetDefault";
import { emptyValueObjects, hasDuplicateValues } from "../../utils/ManageArray";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../FormComponent";
import TableForm from "../TableForm";
import ConfirmModal from "../ConfirmModal";
import SearchFilter from "../SearchFilter";
import CardDisplayComponent from "../CardDisplayComponent";

import ItemsService from "../../services/item.service";
import ParentBomsService from "../../services/parentboms.service";
import ChildBomsService from "../../services/childboms.service";

const BOMForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [parentBoms, setParentBoms] = useState([]);
  const [childBoms, setChildBoms] = useState([]);
  const [itemParent, setItemParent] = useState([]);
  const [itemChild, setItemChild] = useState([]);
  const defaultData = {
    Parent: { Item_Parent_Id: "", Bom_Type: "", Com_Code: user.Com_Code },
    Child: {
      Parent_Bom_Id: "",
      Item_Child_Id: "",
      Item_Child_Qty: "",
      Foc_Id: 1,
      Bom_Pm: "",
      Sub_Bom: ""
    }
  };
  const [data, setData] = useState({
    Parent: { Array: [defaultData.Parent] },
    Child: { Array: [defaultData.Child] }
  });
  const searchInput = [
    { name: "Item.Item_Code", label: "รหัสสินค้า" },
    { name: "Item.Item_Name_TH", label: "ชื่อสินค้า", size: 4 },
    { name: "Bom_Type", label: "ชนิด BOM", size: 2, noneAsync: true }
  ];
  const inputField = {
    Parent: [
      {
        name: "Item_Parent_Id",
        label: "รหัสสินค้า",
        type: "select",
        filterBy: ["Item_Code", "Item_Name_TH"],
        options: initializeSetOptionDisabled(
          {
            key: "Id",
            label: ["Item_Code", "Item_Name_TH"],
            type: "Multi",
            dataset: itemParent
          },
          { key: "Item_Parent_Id", data: parentBoms }
        ),
        queryService: (query) =>
          getServiceRequest({
            showAlert,
            setLoading,
            serviceRequest: ItemsService.getAllItems(query),
            callback: ({ items }) => setItemParent(items)
          })
      },
      {
        name: "Item_Name_TH",
        label: "ชื่อสินค้า",
        type: "text",
        size: editData?.type === "copy" ? 4 : 6,
        readOnly: true,
        findValue: { data: itemParent, key: "Id", value: "Item_Parent_Id" }
      },
      {
        name: "Bom_Type",
        label: "ชนิด BOM",
        type: "select",
        size: editData?.type === "copy" ? 2 : null,
        noneAsync: true,
        options: [
          { value: "FG", label: "FG" },
          { value: "DRAFT", label: "Draft" }
        ]
      }
    ],
    Child: [
      {
        name: "Item_Child_Id",
        label: "รหัสอะไหล่",
        type: "select",
        size: 2,
        filterBy: ["Item_Code", "Item_Name_TH"],
        options: initializeSetOptionDisabled(
          {
            key: "Id",
            label: ["Item_Code", "Item_Name_TH"],
            type: "Multi",
            dataset: itemChild
          },
          {
            key: "Item_Parent_Id",
            value: data.Parent.Array[0]?.Item_Parent_Id,
            data: parentBoms
          }
        ),
        queryService: (query) =>
          getServiceRequest({
            showAlert,
            setLoading,
            serviceRequest: ItemsService.getAllItems(query),
            callback: ({ items }) => setItemChild(items)
          })
      },
      {
        name: "Item_Name_TH",
        label: "ชื่ออะไหล่",
        type: "text",
        readOnly: true,
        findValue: { data: itemChild, key: "Id", value: "Item_Child_Id" }
      },
      {
        name: "Item_Model",
        label: "Model Number",
        type: "text",
        size: "1_5",
        readOnly: true,
        findValue: { data: itemChild, key: "Id", value: "Item_Child_Id" }
      },
      { name: "Item_Child_Qty", label: "จำนวน", type: "text", size: 1 },
      {
        name: "Unit",
        label: "หน่วยนับ",
        type: "text",
        size: 1,
        readOnly: true,
        findValue: { data: itemChild, key: "Id", value: "Item_Child_Id" }
      },
      {
        name: "Bom_Pm",
        label: "เช็ค/เปลี่ยน",
        type: "select",
        size: "1_5",
        noneAsync: true,
        options: [
          { value: true, label: "Y" },
          { value: false, label: "N" }
        ]
      },
      {
        name: "Sub_Bom",
        label: "SUB BOM",
        type: "select",
        size: "1_5",
        noneAsync: true,
        options: [
          { value: true, label: "Y" },
          { value: false, label: "N" }
        ]
      }
    ]
  };

  const handleChange = (name, value) => {
    setData((prev) => ({
      ...prev,
      Parent: {
        Array: [
          { ...prev.Parent.Array[0], [name]: value },
          ...prev.Parent.Array.slice(1)
        ]
      }
    }));
  };

  const handleSearchFilter = (searchFilter) => {
    const parentFiltered = parentBoms
      .filter((parent) =>
        searchFilter.every(
          ({ name, value }) =>
            !value || formatSplitKey(name, ".", parent) === value
        )
      )
      .map(({ Id }) => ({ name: "Parent_Bom_Id", value: Id }));

    if (parentFiltered.length > 1)
      return showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณาเลือกข้อมูลรหัสสินค้าหรือชื่อสินค้าก่อน",
        type: "danger"
      });

    const childFiltered = childBoms.filter((child) =>
      parentFiltered.some(({ name, value }) => value === child[name])
    );

    childFiltered.map(({ Item }) => setItemChild((prev) => [...prev, Item]));

    setData((prev) => ({
      ...prev,
      Child: {
        Array: childFiltered?.length ? childFiltered : [defaultData.Child]
      }
    }));
  };

  const checkValidity = () => {
    if (hasDuplicateValues(data.Child.Array, ["Item_Child_Id"])) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "รหัสอะไหล่ซ้ำกัน กรุณาเลือกรหัสอะไหล่ใหม่อีกครั้ง",
        type: "danger"
      });
      return false;
    }

    const passEmptyKey = ["Item_Name_TH", "Parent_Bom_Id", "Unit"];
    if (
      (emptyValueObjects(data.Parent.Array).length > 0 &&
        editData?.type !== "edit") ||
      emptyValueObjects(data.Child.Array, passEmptyKey).length > 0
    ) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(true);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) onSubmitCallback(dataCallback);
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ParentBomsService.getAllParentBoms(),
      callback: (res) => setParentBoms(res)
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: ChildBomsService.getAllChildBoms(),
      callback: (res) => setChildBoms(res)
    });
  }, []);

  return (
    <>
      {editData && (
        <CardDisplayComponent
          header={
            editData?.type === "copy"
              ? {
                  title: {
                    text: "ค้นหารายการที่จะคัดลอก",
                    className: "text-center"
                  }
                }
              : null
          }
          body={{
            element: (
              <SearchFilter
                data={parentBoms}
                inputField={searchInput}
                fieldClassName={{ actionButton: "w-auto py-2" }}
                searchCallback={(searchCB) =>
                  searchCB.some(({ value }) => value)
                    ? handleSearchFilter(searchCB)
                    : setData((prev) => ({
                        ...prev,
                        Child: { Array: [defaultData.Child] }
                      }))
                }
              />
            )
          }}
        />
      )}

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        {(!editData || editData.type === "copy") && (
          <CardDisplayComponent
            header={
              editData?.type === "copy"
                ? {
                    title: {
                      className: "text-center",
                      text: "เลือกสินค้าและชนิด BOM ที่สร้าง"
                    }
                  }
                : null
            }
            body={{
              as: Row,
              className: "justify-content-center align-items-center",
              element: (
                <>
                  {inputField.Parent.map(
                    ({
                      name,
                      label,
                      type,
                      size,
                      options,
                      findValue,
                      queryService,
                      noneAsync
                    }) => {
                      const dataParent = data.Parent.Array?.[0];
                      const value = findValue
                        ? autoDisplayValue({
                            name,
                            type,
                            findValue: {
                              ...findValue,
                              value: dataParent?.[findValue?.value]
                            }
                          })
                        : "";

                      return (
                        <Col key={name} sm={size || 3}>
                          {type === "select" ? (
                            <SearchSelectComponent
                              labelField={label}
                              options={options}
                              placeholder="โปรดเลือก"
                              noneAsync={noneAsync}
                              queryService={queryService}
                              isInvalid={data.Parent.Array[0][name] === ""}
                              onChange={(change) =>
                                handleChange(name, change?.value || null)
                              }
                              value={`${dataParent?.[name]}`}
                            />
                          ) : (
                            <FloatingControlComponent
                              readOnly
                              type="text"
                              name={name}
                              label={label}
                              placeholder="โปรดระบุ"
                              value={value}
                            />
                          )}
                        </Col>
                      );
                    }
                  )}

                  {editData?.type === "copy" && (
                    <Col sm={3} style={{ width: "257.35px" }}>
                      <Button
                        type="submit"
                        variant="success"
                        className="me-2 px-4 py-2 text-dark rounded-pill"
                      >
                        บันทึก
                      </Button>
                      <ConfirmModal
                        show={showModal}
                        data={{ data, childBoms }}
                        content="copy"
                        onSubmitCallback={handleSubmit}
                      />

                      <Button
                        variant="outline-secondary"
                        className="px-4 py-2 text-dark rounded-pill"
                        onClick={() =>
                          navigate("/wo_master/BOM", { state: "Bom" })
                        }
                      >
                        ยกเลิก
                      </Button>
                    </Col>
                  )}
                </>
              )
            }}
          />
        )}

        <CardDisplayComponent
          header={{
            title: {
              text:
                editData?.type === "copy"
                  ? "เพิ่ม-แก้ไข-รายละเอียด BOM"
                  : "รายละเอียด BOM"
            }
          }}
          body={{
            element: (
              <TableForm
                data={data.Child.Array}
                columns={inputField.Child}
                defaultData={defaultData.Child}
                duplicateKey={["Item_Child_Id"]}
                activeButton={
                  !!editData ? ["add", "copy"] : ["add", "copy", "delete"]
                }
                onChangeCallback={(newData) =>
                  setData((prev) => ({
                    ...prev,
                    Parent: {
                      Array: data.Parent.Array.map((item) => ({
                        ...item,
                        Item_Parent_Id: Number(item.Item_Parent_Id) || ""
                      }))
                    },
                    Child: {
                      Array: newData.map((item) => ({
                        ...item,
                        Item_Child_Id: Number(item.Item_Child_Id) || "",
                        Item_Child_Qty: Number(item.Item_Child_Qty) || ""
                      }))
                    }
                  }))
                }
              />
            )
          }}
          footer={{
            className: "text-end",
            element:
              editData?.type !== "copy" ? (
                <>
                  <Button
                    type="submit"
                    variant="success"
                    className="me-2 px-5 py-2 text-dark rounded-pill"
                  >
                    {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                  </Button>
                  <ConfirmModal
                    show={showModal}
                    data={{ data, childBoms }}
                    content={editData ? "edit" : "save"}
                    onSubmitCallback={handleSubmit}
                  />

                  <Button
                    variant="outline-secondary"
                    className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                    onClick={() => navigate("/wo_master/BOM", { state: "Bom" })}
                  >
                    ยกเลิก
                  </Button>
                </>
              ) : (
                <></>
              )
          }}
        />
      </Form>
    </>
  );
};

BOMForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default BOMForm;
